import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import TextAreaAutosize from './components/textarea-autosize/TextAreaAutosize';
import AttachmentsTray from './components/attachments-tray/AttachmentsTray';
import WithError from '../with-error/WithError';

import scss from './ChatInput.module.scss';

const ChatInput = forwardRef(function ChatInput(
  {
    value,
    onChange,
    onFocus,
    onBlur,
    onSend,

    placeholder,
    disabled = false,
    isSending = false,
    isError = false,
    errorText = '',

    attachments = [],
    onAddAttachments = null,
    onRemoveAttachment = null,
    attachmentsMode = 'overlay',
    attachmentsPosition = 'top',

    leftActions = null,
    rightActions = null,

    maxTextareaHeight = 120,
    showHint = true,
  },
  ref,
) {
  const { t } = useTranslation();
  const textareaRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus?.(),
    blur: () => textareaRef.current?.blur?.(),
    getElement: () => textareaRef.current,
  }));

  const canSend = useMemo(() => {
    if (disabled || isSending) return false;
    return (value || '').trim().length > 0;
  }, [value, disabled, isSending]);

  const handleKeyDown = (e) => {
    if (e.isComposing) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!canSend) return;
      onSend?.();
    }
  };

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData.files);
    if (!files.length) return;

    onAddAttachments?.(files);
    e.preventDefault();
  };

  const handleWrapperMouseDown = (e) => {
    const target = e.target;
    const isInteractive = target?.closest?.('button, a, [role="button"]');
    if (isInteractive) return;

    const isTextarea = target === textareaRef.current;
    if (isTextarea) return;

    e.preventDefault();
    textareaRef.current?.focus?.();
  };

  return (
    <>
      <AttachmentsTray
        items={attachments}
        onRemove={onRemoveAttachment}
        mode={attachmentsMode}
        position={attachmentsPosition}
      />

      <div
        className={scss.root}
        data-disabled={disabled ? 'true' : 'false'}
        data-sending={isSending ? 'true' : 'false'}
        data-has-left={leftActions ? 'true' : 'false'}
        data-has-right={rightActions ? 'true' : 'false'}
        data-error={isError ? 'true' : 'false'}
        onMouseDown={handleWrapperMouseDown}
        role='presentation'>
        {leftActions ? (
          <div className={scss.left}>{leftActions({ canSend, isSending, value, onChange, disabled })}</div>
        ) : null}

        <WithError isShowError={isError} errorText={errorText} isAbsolute extraClassName={scss.errorWrapper}>
          <div className={scss.center}>
            <TextAreaAutosize
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              onFocus={onFocus}
              onBlur={onBlur}
              placeholder={placeholder}
              disabled={disabled}
              maxHeight={maxTextareaHeight}
              rows={1}
              className={scss.textarea}
            />

            {showHint ? (
              <div className={scss.hint}>
                <span>{t('enterToSend')}</span>
                <span className={scss.dot}>•</span>
                <span>{t('shiftEnterForNewline')}</span>
              </div>
            ) : null}
          </div>
        </WithError>

        {rightActions ? (
          <div className={scss.right}>{rightActions({ canSend, isSending, value, onChange, disabled })}</div>
        ) : null}
      </div>
    </>
  );
});

export default ChatInput;
